// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xPartNumberStyle } from "@keller/Overrides/Components/ProductPartNumbers";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ReorderPadProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ReorderPadProductAttributesStyles {
    container?: InjectableCss;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const reorderPadProductAttributesStyles: ReorderPadProductAttributesStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    attributeTypeText: {
        ...xPartNumberStyle.label,
    },
    attributeValueText: {
        ...xPartNumberStyle.value,
    },
    wrapper: {
        css: css`
            display: flex;
        `,
    },
};

const ReorderPadProductAttributes: FC<Props> = ({ product: { properties }, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(reorderPadProductAttributesStyles, extendedStyles));

    if (!properties.lastOrderedQty && !properties.lastOrderedDate) {
        return null;
    }

    const displayAttribute = (label: string, value?: string) => {
        if (!value) {
            return null;
        }
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.attributeTypeText}>{label}</Typography>
                <Typography {...styles.attributeValueText}>{value}</Typography>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            {displayAttribute(translate("Order QTY"), properties.lastOrderedQty)}
            {displayAttribute(translate("Last Ordered"), properties.lastOrderedDate)}
        </StyledWrapper>
    );
};

export default withProduct(ReorderPadProductAttributes);
